import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { advancedLocalStorage } from '@sendible/common';
import { useBridgeUser } from '@sendible/shared-state-bridge';
import { EVENTS, subscribe } from '@sendible/event-manager';
import { getTodayISO, shiftMonth, shiftWeek } from './utils';

const VIEW_MODE_STORAGE_KEY = 'newcalendar_mode';
const DENSITY_STORAGE_KEY = 'newcalendar_density';
const DAILY_SORT_ORDER_STORAGE_KEY = 'newcalendar_daily_sort_order';
const POSTS_PER_DAY_STORAGE_KEY = 'newcalendar_posts_per_day';
const HIDE_EMPTY_DATES_STORAGE_KEY = 'newcalendar_hide_empty_dates';

export const POSTS_PER_DAY_LIMITS: Record<CalendarDensity, { min: number; max: number }> = {
  comfortable: { min: 1, max: 5 },
  compact: { min: 3, max: 8 },
};

const DENSITIES = Object.keys(POSTS_PER_DAY_LIMITS) as CalendarDensity[];

export const clampPostsPerDay = (value: number, density: CalendarDensity) => {
  const { min, max } = POSTS_PER_DAY_LIMITS[density];

  return Number.isFinite(value) ? Math.min(max, Math.max(min, Math.round(value))) : min;
};

type PostsPerDayByDensity = Record<CalendarDensity, number>;

const readPostsPerDay = (view: string): PostsPerDayByDensity =>
  DENSITIES.reduce((acc, density) => {
    const stored = advancedLocalStorage.getItem(`${POSTS_PER_DAY_STORAGE_KEY}_${view}_${density}`);

    acc[density] = clampPostsPerDay(stored === null || stored === undefined ? NaN : Number(stored), density);

    return acc;
  }, {} as PostsPerDayByDensity);

// The daily view has never shown dates without messages, so hiding them is the default.
const readHideEmptyDates = (view: string) => advancedLocalStorage.getItem(`${HIDE_EMPTY_DATES_STORAGE_KEY}_${view}`) !== 'false';

type SidebarMode = 'day' | 'message';
export type DailySortOrder = 'oldest' | 'newest';

const VIEW_DEFAULTS: Record<string, { viewMode: CalendarView; density: CalendarDensity; sortOrder: DailySortOrder }> = {
  main: { viewMode: 'monthly', density: 'comfortable', sortOrder: 'oldest' },
  scheduled: { viewMode: 'daily', density: 'comfortable', sortOrder: 'oldest' },
  sent: { viewMode: 'daily', density: 'comfortable', sortOrder: 'newest' },
  failed: { viewMode: 'daily', density: 'comfortable', sortOrder: 'newest' },
};

type CalendarContextType = {
  changeSidebarMode: (type: SidebarMode, day: CalendarMonthString, messageId?: CalendarMessage['id']) => void;
  closeSidebar: () => void;
  currentDate: CalendarMonthString;
  density: CalendarDensity;
  dailySortOrder: DailySortOrder;
  filterParams: Record<string, string>;
  hideEmptyDates: boolean;
  isExporting: boolean;
  isFetching: boolean;
  nextMonth: () => void;
  nextWeek: () => void;
  openSidebar: (type: SidebarMode, day: CalendarMonthString, messageId?: CalendarMessage['id']) => void;
  postsPerDay: number;
  prevMonth: () => void;
  prevWeek: () => void;
  setCurrentDate: Dispatch<SetStateAction<CalendarMonthString>>;
  setCurrentDateToday: () => void;
  setDensity: Dispatch<SetStateAction<CalendarDensity>>;
  setDailySortOrder: Dispatch<SetStateAction<DailySortOrder>>;
  setFilterParams: Dispatch<SetStateAction<Record<string, string>>>;
  setPostsPerDay: (value: number) => void;
  setHideEmptyDates: Dispatch<SetStateAction<boolean>>;
  setIsExporting: Dispatch<SetStateAction<boolean>>;
  setIsFetching: Dispatch<SetStateAction<boolean>>;
  setUserWeekStartDay: Dispatch<SetStateAction<UserWeekStartDay>>;
  setViewMode: Dispatch<SetStateAction<CalendarView>>;
  setShowHolidays: Dispatch<SetStateAction<boolean>>;
  showHolidays: boolean;
  todayDate: CalendarMonthString;
  viewMode: CalendarView;
  userWeekStartDay: UserWeekStartDay;
};

type CalendarSidebarContextType = {
  isSidebarOpen: boolean;
  lastSidebarMode: SidebarMode | undefined;
  setSidebarDay: Dispatch<SetStateAction<CalendarMonthString>>;
  sidebarDay: CalendarMonthString;
  sidebarMessageId: CalendarMessage['id'] | undefined;
  sidebarMode: SidebarMode;
};

export const CalendarContext = createContext({} as CalendarContextType);
export const CalendarSidebarContext = createContext({} as CalendarSidebarContextType);

export const CalendarProvider = ({ children }: Component) => {
  const user = useBridgeUser();
  const timezoneOffset = user?.timezone ?? 0;
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const activeView = (['main', 'scheduled', 'sent', 'failed'] as const).includes(viewParam as never) ? (viewParam as string) : 'main';
  const activeViewRef = useRef(activeView);
  const [todayDate, setTodayDate] = useState<CalendarMonthString>(() => getTodayISO(timezoneOffset));
  const [filterParams, setFilterParams] = useState<Record<string, string>>({});
  const [currentDate, setCurrentDate] = useState<CalendarMonthString>(todayDate);

  const [viewMode, setViewMode] = useState<CalendarView>(() => {
    const defaults = VIEW_DEFAULTS[activeView] ?? VIEW_DEFAULTS.main;

    return (advancedLocalStorage.getItem(`${VIEW_MODE_STORAGE_KEY}_${activeView}`) as CalendarView) || defaults.viewMode;
  });

  const [density, setDensity] = useState<CalendarDensity>(() => {
    const defaults = VIEW_DEFAULTS[activeView] ?? VIEW_DEFAULTS.main;

    return (advancedLocalStorage.getItem(`${DENSITY_STORAGE_KEY}_${activeView}`) as CalendarDensity) || defaults.density;
  });

  const [dailySortOrder, setDailySortOrder] = useState<DailySortOrder>(() => {
    const defaults = VIEW_DEFAULTS[activeView] ?? VIEW_DEFAULTS.main;

    return (advancedLocalStorage.getItem(`${DAILY_SORT_ORDER_STORAGE_KEY}_${activeView}`) as DailySortOrder) || defaults.sortOrder;
  });

  const [postsPerDayByDensity, setPostsPerDayByDensity] = useState<PostsPerDayByDensity>(() => readPostsPerDay(activeView));
  const postsPerDay = postsPerDayByDensity[density];

  const setPostsPerDay = useCallback(
    (value: number) => {
      const next = clampPostsPerDay(value, density);

      setPostsPerDayByDensity((prev) => ({ ...prev, [density]: next }));
      advancedLocalStorage.setItem(`${POSTS_PER_DAY_STORAGE_KEY}_${activeViewRef.current}_${density}`, String(next));
    },
    [density]
  );
  const [hideEmptyDates, setHideEmptyDates] = useState<boolean>(() => readHideEmptyDates(activeView));

  const [isFetching, setIsFetching] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('day');
  const [lastSidebarMode, setLastSidebarMode] = useState<SidebarMode | undefined>(undefined);
  const [sidebarDay, setSidebarDay] = useState<CalendarMonthString>(todayDate);
  const [sidebarMessageId, setSidebarMessageId] = useState<CalendarMessage['id']>(0);
  const [userWeekStartDay, setUserWeekStartDay] = useState<UserWeekStartDay>('sunday');
  const [showHolidays, setShowHolidays] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState(false);
  const clearMessagesTimeout = useRef<number | null>(null);

  const todayDateRef = useRef(todayDate);
  const sidebarModeRef = useRef(sidebarMode);

  const [prevActiveView, setPrevActiveView] = useState(activeView);

  if (activeView !== prevActiveView) {
    setPrevActiveView(activeView);

    const defaults = VIEW_DEFAULTS[activeView] ?? VIEW_DEFAULTS.main;

    setViewMode((advancedLocalStorage.getItem(`${VIEW_MODE_STORAGE_KEY}_${activeView}`) as CalendarView) || defaults.viewMode);
    setDensity((advancedLocalStorage.getItem(`${DENSITY_STORAGE_KEY}_${activeView}`) as CalendarDensity) || defaults.density);
    setDailySortOrder((advancedLocalStorage.getItem(`${DAILY_SORT_ORDER_STORAGE_KEY}_${activeView}`) as DailySortOrder) || defaults.sortOrder);
    setPostsPerDayByDensity(readPostsPerDay(activeView));
    setHideEmptyDates(readHideEmptyDates(activeView));
  }

  useEffect(() => {
    todayDateRef.current = todayDate;
    sidebarModeRef.current = sidebarMode;
  }, [todayDate, sidebarMode]);

  const closeSidebar = useCallback(() => {
    setSidebarMode('day');
    setSidebarDay(todayDateRef.current);
    setSidebarMessageId(0);
    setIsSidebarOpen(false);
    if (clearMessagesTimeout.current) clearTimeout(clearMessagesTimeout.current);
    clearMessagesTimeout.current = window.setTimeout(() => {
      clearMessagesTimeout.current = null;
    }, 500);
  }, []);

  const changeSidebarMode = useCallback((type: SidebarMode, day: CalendarMonthString, messageId?: CalendarMessage['id']) => {
    setLastSidebarMode(sidebarModeRef.current);
    if (messageId) setSidebarMessageId(messageId);
    setSidebarDay(day);
    setSidebarMode(type);
  }, []);

  const openSidebar = useCallback((type: SidebarMode, day: CalendarMonthString, messageId?: CalendarMessage['id']) => {
    setLastSidebarMode(undefined);
    if (messageId) setSidebarMessageId(messageId);
    setSidebarDay(day);
    setSidebarMode(type);
    setIsSidebarOpen(true);
  }, []);

  const setCurrentDateToday = useCallback(() => {
    window.dispatchEvent(new Event('calendar:goToToday'));
    setCurrentDate(todayDateRef.current);
  }, []);
  const nextMonth = useCallback(() => {
    window.dispatchEvent(new Event('calendar:goToNextMonth'));
    setCurrentDate((d) => shiftMonth(d, 1));
  }, []);
  const prevMonth = useCallback(() => {
    window.dispatchEvent(new Event('calendar:goToPrevMonth'));
    setCurrentDate((d) => shiftMonth(d, -1));
  }, []);
  const nextWeek = useCallback(() => setCurrentDate((d) => shiftWeek(d, 1)), []);
  const prevWeek = useCallback(() => setCurrentDate((d) => shiftWeek(d, -1)), []);

  useEffect(() => {
    const refreshToday = () => {
      const newToday = getTodayISO(timezoneOffset);

      setTodayDate((prev) => (prev !== newToday ? newToday : prev));
    };

    const handleTimezoneChanged = () => {
      const freshOffset = window.sharedState?.user?.timezone ?? 0;
      const newToday = getTodayISO(freshOffset);

      setTodayDate((prev) => (prev !== newToday ? newToday : prev));
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshToday();
    };

    const unsubscribeTimezoneChanged = subscribe(EVENTS.TIMEZONE_CHANGED, handleTimezoneChanged);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const intervalId = window.setInterval(refreshToday, 60_000);

    return () => {
      unsubscribeTimezoneChanged();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(intervalId);
      if (clearMessagesTimeout.current) clearTimeout(clearMessagesTimeout.current);
    };
  }, [timezoneOffset]);

  useEffect(() => {
    activeViewRef.current = activeView;
  }, [activeView]);

  useEffect(() => {
    advancedLocalStorage.setItem(`${VIEW_MODE_STORAGE_KEY}_${activeViewRef.current}`, viewMode);
  }, [viewMode]);

  useEffect(() => {
    advancedLocalStorage.setItem(`${DENSITY_STORAGE_KEY}_${activeViewRef.current}`, density);
  }, [density]);

  useEffect(() => {
    advancedLocalStorage.setItem(`${DAILY_SORT_ORDER_STORAGE_KEY}_${activeViewRef.current}`, dailySortOrder);
  }, [dailySortOrder]);

  useEffect(() => {
    advancedLocalStorage.setItem(`${HIDE_EMPTY_DATES_STORAGE_KEY}_${activeViewRef.current}`, String(hideEmptyDates));
  }, [hideEmptyDates]);

  const gridContextValue = useMemo(
    () => ({
      changeSidebarMode,
      closeSidebar,
      currentDate,
      density,
      dailySortOrder,
      filterParams,
      hideEmptyDates,
      isExporting,
      isFetching,
      nextMonth,
      nextWeek,
      openSidebar,
      postsPerDay,
      prevMonth,
      prevWeek,
      setCurrentDate,
      setCurrentDateToday,
      setDensity,
      setDailySortOrder,
      setFilterParams,
      setPostsPerDay,
      setHideEmptyDates,
      setIsExporting,
      setIsFetching,
      setUserWeekStartDay,
      setViewMode,
      setShowHolidays,
      showHolidays,
      todayDate,
      userWeekStartDay,
      viewMode,
    }),
    [
      changeSidebarMode,
      closeSidebar,
      nextMonth,
      nextWeek,
      openSidebar,
      prevMonth,
      prevWeek,
      setCurrentDateToday,
      currentDate,
      density,
      dailySortOrder,
      filterParams,
      hideEmptyDates,
      isExporting,
      isFetching,
      postsPerDay,
      setPostsPerDay,
      showHolidays,
      todayDate,
      userWeekStartDay,
      viewMode,
    ]
  );

  const sidebarContextValue = useMemo(
    () => ({
      isSidebarOpen,
      lastSidebarMode,
      setSidebarDay,
      sidebarDay,
      sidebarMessageId,
      sidebarMode,
    }),
    [isSidebarOpen, lastSidebarMode, sidebarDay, sidebarMessageId, sidebarMode]
  );

  return (
    <CalendarContext.Provider value={gridContextValue}>
      <CalendarSidebarContext.Provider value={sidebarContextValue}>{children}</CalendarSidebarContext.Provider>
    </CalendarContext.Provider>
  );
};

export const useCalendarContext = (): CalendarContextType => useContext(CalendarContext);
export const useCalendarSidebarContext = (): CalendarSidebarContextType => useContext(CalendarSidebarContext);
